
import React from 'react';
import { useInView, slideUpAnimation } from '../utils/animations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const FAQ = () => {
  const [sectionRef, isInView] = useInView({ threshold: 0.1 });
  const isMobile = useIsMobile();
  
  const faqs = [
    {
      question: "Czy możesz nam pokazać cały reportaż z jednego ślubu, a nie tylko wybrane perełki z Instagrama?",
      answer: "Oczywiście! Rozumiemy, że chcecie zobaczyć pełny materiał, by mieć realne wyobrażenie o naszej pracy. Chętnie prześlemy Wam przykładowy reportaż z prawdziwego ślubu — wystarczy napisać do nas maila."
    },
    {
      question: "Co, jeśli wesele się przedłuży — doliczasz wtedy dodatkowe godziny?",
      answer: "Zdarza się, że niektóre momenty warto utrwalić, nawet jeśli dzień się wydłuży. Dlatego dajcie nam znać w trakcie wesela, jeśli chcecie, żebyśmy zostali dłużej. Koszt dodatkowej godziny jest zapisany w umowie i możecie zdecydować o tym na bieżąco. Jesteśmy elastyczni i doskonale rozumiemy, że tego dnia najważniejsze są wspomnienia — nie zegarek."
    },
    {
      question: "Czy masz doświadczenie w pracy z parami, które nie czują się swobodnie przed aparatem?",
      answer: "Absolutnie — większość par, z którymi pracujemy, to osoby, które po raz pierwszy stają przed profesjonalnym obiektywem i zupełnie nie czują się \"jak ryba w wodzie\" przed aparatem. Doskonale to rozumiemy, bo sami również wiemy, jak stresujące potrafi być pozowanie. Naszą rolą jest stworzyć atmosferę, w której szybko zapomnicie o tym, że ktoś Was fotografuje. Pracujemy tak, by wywołać szczere emocje, naturalne uśmiechy i uchwycić Waszą prawdziwą relację — bez sztuczności i napięcia."
    },
    {
      question: "Czy retuszujesz zdjęcia tak, żeby wyglądać lepiej, np. usuwasz zmarszczki, wyszczuplasz sylwetkę?",
      answer: "Na naszych zdjęciach stawiamy przede wszystkim na naturalność i ponadczasowy charakter. Retusz jest delikatny i subtelny — obejmuje wygładzenie skóry, usunięcie pojedynczych niedoskonałości, takich jak pryszcze czy zadrapania. Nie zmieniamy jednak rysów twarzy, nie usuwamy zmarszczek i nie modelujemy sylwetki. Wierzymy, że piękno tkwi w autentyczności, a naszym zadaniem jest uchwycenie Was w najlepszym świetle — nie tworzenie sztucznego obrazu."
    },
    {
      question: "Dlaczego Twoje usługi są droższe niż u innych fotografów?",
      answer: "Cena naszych usług wynika z jakości, doświadczenia i bezpieczeństwa, jakie Wam oferujemy. Inwestujemy w najwyższej klasy sprzęt i zawsze pracujemy z backupem, bo wiemy, że tego dnia nie ma miejsca na pomyłki. Regularnie bierzemy udział w szkoleniach i warsztatach, by stale podnosić nasze umiejętności. Działamy legalnie, płacimy podatki i ZUS, a poza dniem ślubu spędzamy dziesiątki godzin na selekcji, obróbce i przygotowaniu materiału na najwyższym poziomie. To wszystko przekłada się na cenę — i na efekt końcowy, który otrzymujecie."
    },
    {
      question: "Czy ingerujesz w przebieg dnia ślubu, ustawiasz nas i reżyserujesz sceny?",
      answer: "Nasze podejście opiera się przede wszystkim na reportażu i obserwacji — skupiamy się na tym, by dokumentować dzień takim, jaki jest, bez ingerencji. Uważamy, że prawdziwe emocje są najpiękniejsze i nie potrzebują reżyserii. Wyjątkiem są sytuacje, w których prosicie nas o wskazówki — np. podczas sesji plenerowej czy zdjęć grupowych, gdzie pomagamy się ustawić i zadbamy, by każdy wyglądał korzystnie. Poza tym — po prostu pozwalamy wydarzeniom toczyć się swoim rytmem."
    },
    {
      question: "Czy macie licencję potrzebną do fotografowania w kościele?",
      answer: "Tak, posiadamy aktualną licencję wydawaną przez Kurię, która uprawnia nas do fotografowania podczas ceremonii liturgicznych. Mamy również duże doświadczenie w pracy w kościołach — znamy zasady, szanujemy miejsce i sakralny charakter wydarzenia. Dzięki temu poruszamy się dyskretnie i nie zakłócamy przebiegu ceremonii."
    },
    {
      question: "Dlaczego tak długo czekamy na zdjęcia, skoro inni oddają w dwa tygodnie?",
      answer: "Nie pracujemy w pośpiechu, bo zależy nam, by każde zdjęcie było dopracowane w najmniejszym detalu. Selekcja i obróbka materiału to dla nas bardzo ważny etap — dzięki temu otrzymujecie spójny, piękny reportaż, który będzie Was cieszył przez lata. Gotowy materiał otrzymacie w ciągu 60 dni od ślubu, ale już kilka dni po uroczystości dzielimy się z Wami kilkoma kadrami, by umilić Wam czas oczekiwania."
    }
  ];

  return (
    <section id="faq" className="section bg-elegant-50">
      <div 
        className={`container-custom ${slideUpAnimation(isInView)}`} 
        ref={sectionRef}
      >
        <div className="text-center mb-6">
          <h2 className="section-title text-3xl md:text-4xl mb-2">Często Zadawane Pytania</h2>
          <p className="text-elegant-500 font-sans font-light uppercase tracking-wider flex items-center justify-center gap-2 text-base mb-8">
            <HelpCircle size={16} className="text-elegant-500" />
            Odpowiedzi na najczęściej zadawane pytania
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-elegant-200 rounded-lg px-4 overflow-hidden bg-white"
              >
                <AccordionTrigger className="py-3 text-left font-serif hover:no-underline text-base md:text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="py-3 text-elegant-600 text-left text-xs md:text-sm leading-relaxed md:leading-loose">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
